import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Options } from 'types';
import { FormError } from 'components';
import { Close16 } from '@carbon/icons-react';
import {
  DatePicker,
  DatePickerInput,
  Button,
  Form,
  TextInput,
  Checkbox,
  Dropdown
} from 'carbon-components-react';
import { useFormik } from 'formik';
import { SearchEnquiriesSchema } from 'utilities/validation';
import Utils from 'utilities/helper.util';
import { uniq, without, includes } from 'lodash';

const enquiryAttributeOption: Options = [
  {
    label: 'Job start date',
    value: 'start_at'
  },
  {
    label: 'Enquiry lodged (default)',
    value: 'enquiry_at'
  }
];

const datesOption1: Options = [
  {
    label: 'Last 90 days',
    value: 'last_90'
  },
  {
    label: 'Last 30 days (default)',
    value: 'last_30'
  },
  {
    label: 'Last 7 days',
    value: 'last_7'
  },
  {
    label: 'Today',
    value: 'today'
  },
  {
    label: 'Next 7 days',
    value: 'next_7'
  },
  {
    label: 'Next 30 days',
    value: 'next_30'
  },
  {
    label: 'Next 90 days',
    value: 'next_90'
  },
  {
    label: 'Custom range ...',
    value: 'custom'
  }
];

const datesOption2: Options = [
  {
    label: 'Last 90 days',
    value: 'last_90'
  },
  {
    label: 'Last 30 days (default)',
    value: 'last_30'
  },
  {
    label: 'Last 7 days',
    value: 'last_7'
  },
  {
    label: 'Today',
    value: 'today'
  },
  {
    label: 'Custom range ...',
    value: 'custom'
  }
];

const ApplyFilterForm: React.FC<Props> = ({
  handleFilter,
  filterValues,
  resetValues,
  setFilterValues,
  handleOpenCloseFilter,
  containerRef,
  parentOverflow,
  currentTeamId,
  enquirerOption,
  calendarRef
}: any) => {
  const mapSearchWithExtentZoomRef = useRef(false);
  const [startDateMaxDate, setStartDateMaxDate] = useState<string>('');
  const [endDateMaxDate, setEndDateMaxDate] = useState<string>('');
  const {
    touched,
    errors,
    values,
    handleSubmit,
    handleChange,
    handleBlur,
    resetForm,
    setFieldValue
  } = useFormik({
    enableReinitialize: true,
    initialValues: filterValues,
    validationSchema: SearchEnquiriesSchema,
    onSubmit: (values: any) => {
      handleFilter(values, mapSearchWithExtentZoomRef.current);
    }
  });

  const getOptionItem = (type: string, value: string) => {
    if (type === 'enquiryAttributes') {
      return enquiryAttributeOption.find((o) => value === o.value);
    } else if (type === 'enquirer') {
      return enquirerOption.find((o: any) => {
        if (!value) return '0';
        return value === o.value;
      });
    } else {
      if (values.enquiryAttributes === 'start_at') {
        return datesOption1.find((o) => value === o.value);
      } else {
        return datesOption2.find((o) => value === o.value);
      }
    }
  };

  const handleResetFilter = () => {
    resetValues();
    resetForm();
  };

  const updateFilterValues = useCallback(
    (updatedValues: object) => {
      setFilterValues({
        ...values,
        ...updatedValues
      });
    },
    [values, setFilterValues]
  );

  const handleDropdownChange = (key: any, { selectedItem }: any) => {
    if (key === 'enquiryAttributes' && selectedItem?.value) {
      updateFilterValues({
        enquiryAttributes: selectedItem.value
      });
    } else if (key === 'datesSelection' && selectedItem?.value) {
      updateFilterValues({
        datesSelection: selectedItem.value,
        datesSelectionCustomed: false,
        startDate: undefined,
        endDate: undefined
      });
    } else if (key === 'enquirer' && selectedItem?.value) {
      updateFilterValues({
        enquirer: selectedItem.value
      });
    }
  };

  const handleStatusChange = (value: string, checked: boolean) => {
    let { status } = values;
    if (checked) {
      status.push(value);
      status = uniq(status);
    } else {
      status = without(status, value);
    }
    updateFilterValues({
      status
    });
  };

  const handleDateChange = (key: string, value: any) => {
    if (Array.isArray(value) && value.length > 0) {
      const updatedValue = value[0];
      updateFilterValues({
        [key]: Utils.getNewDate(updatedValue)
      });
    }
  };

  const onChangeDate = (key: string, val: any, setField?: boolean) => {
    setField && setFieldValue(key, Utils.getNewDate(val[0]), true);
    handleDateChange(key, val);
  };

  const onCalendarClose = (val: any, strValue: any, self: any) => {
    strValue && self.setDate(strValue, true, 'd/m/Y');
  };

  useEffect(() => {
    const { datesSelection, startDate, endDate, datesSelectionCustomed } =
      values;
    if (
      datesSelection === 'custom' &&
      startDate &&
      endDate &&
      !datesSelectionCustomed
    ) {
      updateFilterValues({
        datesSelectionCustomed: true
      });
    }

    if (values.enquiryAttributes === 'enquiry_at') {
      const todaysDate = Utils.toLocalDateFormat(Utils.getNewDate());
      setStartDateMaxDate(todaysDate);
      setEndDateMaxDate(todaysDate);
    } else if (
      !values.enquiryAttributes ||
      values.enquiryAttributes === 'start_at'
    ) {
      setStartDateMaxDate('');
      setEndDateMaxDate('');
    }
  }, [values, updateFilterValues]);

  return (
    <div
      className={`search-filter-panel-wrapper ${
        parentOverflow ? 'parent-overflow' : ''
      }`}
    >
      <section ref={containerRef} className="search-filter-panel-main">
        <Form>
          <div className="search-filter-panel-form-wrapper">
            <div className="heading filter-spacing">
              <p>Filter the view</p>
              <Button
                kind="ghost"
                size="sm"
                onClick={() => handleOpenCloseFilter(false)}
              >
                <Close16 />
              </Button>
            </div>
            <div className="divider" />
            <div className="search-filter-panel-date-container  filter-spacing">
              <div className="dropdown-container">
                <Dropdown
                  ariaLabel="ENQUIRY ATTRIBUTE"
                  id="enquiryAttributes"
                  items={enquiryAttributeOption}
                  label="Choose an option"
                  titleText="ENQUIRY ATTRIBUTE"
                  onChange={(val) =>
                    handleDropdownChange('enquiryAttributes', val)
                  }
                  initialSelectedItem={getOptionItem(
                    'enquiryAttributes',
                    values.enquiryAttributes
                  )}
                />
                {values.enquiryAttributes === 'start_at' && (
                  <Dropdown
                    ariaLabel="DATES"
                    id="datesSelection"
                    items={datesOption1}
                    label="Choose an option"
                    titleText="DATES"
                    onChange={(val) =>
                      handleDropdownChange('datesSelection', val)
                    }
                    initialSelectedItem={getOptionItem(
                      'datesSelection',
                      values.datesSelection
                    )}
                  />
                )}
                {values.enquiryAttributes === 'enquiry_at' && (
                  <Dropdown
                    ariaLabel="DATES"
                    id="datesSelection"
                    items={datesOption2}
                    label="Choose an option"
                    titleText="DATES"
                    onChange={(val) =>
                      handleDropdownChange('datesSelection', val)
                    }
                    initialSelectedItem={getOptionItem(
                      'datesSelection',
                      values.datesSelection
                    )}
                  />
                )}
                {touched && errors.datesSelection && (
                  <FormError>{errors.datesSelection}</FormError>
                )}
                {values.datesSelection === 'custom' && (
                  <div className="date-picker-container">
                    <DatePicker
                      dateFormat="d/m/Y"
                      datePickerType="single"
                      value={
                        values.startDate
                          ? Utils.toLocalDateFormat(values.startDate)
                          : ''
                      }
                      onChange={(val: any) => {
                        onChangeDate('startDate', val, false);
                      }}
                      onClose={onCalendarClose}
                      ref={calendarRef}
                      maxDate={startDateMaxDate}
                    >
                      <DatePickerInput
                        id="createdAfter"
                        name="createdAfter"
                        data-testid="createdAfter"
                        labelText="From"
                        placeholder="dd/mm/yyyy"
                        size={'sm'}
                        type="text"
                        autoComplete="off"
                      />
                    </DatePicker>
                    <DatePicker
                      dateFormat="d/m/Y"
                      datePickerType="single"
                      onChange={(val: any) => {
                        onChangeDate('endDate', val, true);
                      }}
                      onClose={onCalendarClose}
                      ref={calendarRef}
                      value={
                        values.endDate
                          ? Utils.toLocalDateFormat(values.endDate)
                          : ''
                      }
                      maxDate={endDateMaxDate}
                    >
                      <DatePickerInput
                        id="createdBefore"
                        name="createdBefore"
                        labelText="To"
                        data-testid="createdBefore"
                        placeholder="dd/mm/yyyy"
                        size={'sm'}
                        type="text"
                        autoComplete="off"
                      />
                    </DatePicker>
                  </div>
                )}
              </div>
              {touched && errors.endDate && (
                <FormError>{errors.endDate}</FormError>
              )}

              {currentTeamId && enquirerOption?.length > 0 && (
                <Dropdown
                  ariaLabel="ENQUIRER"
                  id="enquirer"
                  items={enquirerOption}
                  label="Choose an option"
                  titleText="ENQUIRER"
                  onChange={(val) => handleDropdownChange('enquirer', val)}
                  initialSelectedItem={getOptionItem(
                    'enquirer',
                    values.enquirer
                  )}
                />
              )}
            </div>
            <div className="divider" />
            <div className="bx--fieldset status-checkbox-container filter-spacing">
              <div className="checkbox-div">
                <Checkbox
                  labelText="Waiting"
                  id="status-checked-waiting"
                  onChange={(checked: boolean) =>
                    handleStatusChange('waiting', checked)
                  }
                  checked={includes(values.status, 'waiting')}
                />
              </div>
              <div className="checkbox-div">
                <Checkbox
                  labelText="All received"
                  id="status-checked-completed"
                  onChange={(checked: boolean) =>
                    handleStatusChange('fulfilled', checked)
                  }
                  checked={includes(values.status, 'fulfilled')}
                />
              </div>
              <div className="checkbox-div">
                <Checkbox
                  labelText="Expired"
                  id="status-checked-expired"
                  onChange={(checked: boolean) =>
                    handleStatusChange('expired', checked)
                  }
                  checked={includes(values.status, 'expired')}
                />
              </div>
            </div>
            <div className="divider" />
            <div className="search-filter-panel-input-container filter-spacing">
              <div className="checkbox-div">
                <Checkbox
                  labelText="Archived"
                  id="status-checked-archived"
                  onChange={(checked: boolean) =>
                    handleStatusChange('archived', checked)
                  }
                  checked={includes(values.status, 'archived')}
                />
              </div>
              {/* Temporarily comment out this part */}
              {/* <div className="checkbox-div">
                <Checkbox
                  labelText="Auto-renews"
                  id="status-checked-auto-renews"
                  onChange={(checked: boolean) =>
                    handleStatusChange('auto-renew', checked)
                  }
                  checked={includes(values.status, 'auto-renew')}
                />
              </div> */}
            </div>
            <div className="divider" />
            <div className="search-filter-panel-input-container filter-spacing">
              <div className="filter-input">
                <TextInput
                  id="searchUserRef"
                  name="userReference"
                  labelText="USER REFERENCE CONTAINS TEXT"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.userReference}
                  maxLength={100}
                  data-testid="userReference"
                />
              </div>
            </div>
          </div>
          <div className="search-filter-panel-form-button-container">
            <Button kind="tertiary" onClick={handleResetFilter}>
              Reset
            </Button>
            <Button
              kind="secondary"
              disabled={Object.keys(errors).length > 0}
              onClick={(event: any) => {
                mapSearchWithExtentZoomRef.current = false;
                handleSubmit(event);
              }}
            >
              Apply
            </Button>
          </div>
        </Form>
      </section>
    </div>
  );
};

const propTypes = {
  handleFilter: PropTypes.func.isRequired,
  filterValues: PropTypes.object.isRequired,
  resetValues: PropTypes.func.isRequired,
  setFilterValues: PropTypes.func.isRequired,
  handleOpenCloseFilter: PropTypes.func.isRequired,
  containerRef: PropTypes.object,
  user: PropTypes.any,
  parentOverflow: PropTypes.bool,
  currentTeamId: PropTypes.number,
  enquirerOption: PropTypes.array,
  calendarRef: PropTypes.object
};

type Props = PropTypes.InferProps<typeof propTypes>;

export default ApplyFilterForm;
